import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { useAppState } from "../../hooks/useAppState";
import { useSocketConnected } from "../../hooks/useSocketConnected";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { showToast } from "../../../toast";
import { isVideoFilename } from "../../../utils";
import {
  connectChatSocket,
  onSocketConnectionChange,
  setTogetherSocketHandlers,
  type TogetherAcceptedData,
  type TogetherDeclinedData,
  type TogetherEndedData,
  type TogetherInviteData,
  type TogetherPartnerDisconnectedData,
  type TogetherPartnerReconnectedData,
  type TogetherPauseAskAnsweredData,
  type TogetherPauseAskEndedData,
  type TogetherPauseAskStartedData,
  type TogetherReactionData,
  type TogetherReactionHoldData,
  type TogetherReactionHoldEndData,
  type TogetherScheduleCancelledData,
  type TogetherScheduleCreatedData,
  type TogetherScheduleReadyData,
  type TogetherScheduleStartedData,
  type TogetherScheduleUpdatedData,
} from "../../../chatSocket";
import {
  acceptTogetherApi,
  applyTogetherAccepted,
  applyTogetherDeclined,
  applyTogetherEnded,
  applyTogetherInvite,
  applyTogetherPartnerDisconnected,
  applyTogetherPartnerReconnected,
  applyTogetherPauseAskAnswered,
  applyTogetherPauseAskEnded,
  applyTogetherPauseAskStarted,
  applyTogetherReaction,
  applyTogetherReactionHold,
  applyTogetherReactionHoldEnd,
  applyTogetherScheduleCancelled,
  applyTogetherScheduleCreated,
  applyTogetherScheduleReady,
  applyTogetherScheduleStarted,
  applyTogetherScheduleUpdated,
  declineTogetherApi,
  leaveTogetherApi,
  reactHoldApi,
  reactHoldEndApi,
  reactTogetherApi,
  rejoinTogetherApi,
  removeTogetherReaction,
  startScheduledTogetherApi,
} from "../../../togetherApi";
import { logVideoPlayRejection } from "../../../videoDiagnostics";
import { resolveMediaIndex, useTogetherLiveSync } from "./togetherSync";
import { TogetherInviteDialog } from "./TogetherInviteDialog";
import { ReactionBurst, HoldReactionBadge } from "./ReactionBurst";
import { PauseAndAskOverlay } from "./PauseAndAskOverlay";
import { TogetherRejoinPill } from "./TogetherRejoinPill";
import { TogetherChatPanel } from "./TogetherChatPanel";
import { TogetherChatStream } from "./TogetherChatStream";
import { TogetherChatInput } from "./TogetherChatInput";
import { useTogetherChatMessages } from "./useTogetherChatMessages";
import { AvatarImage } from "../../components/AvatarImage";
import { firstNameOf } from "../home/CoupleHeader";

// Mirrors server.js's ALLOWED_TOGETHER_REACTIONS exactly - the server is
// the real gate (400 invalid_emoji otherwise), this just keeps the tray
// from ever offering something that would be rejected.
const REACTION_EMOJIS = ["❤️", "🔥", "😍", "😂", "😮", "👏"];
// Client-side debounce is purely a UX nicety (visually disable the tray
// briefly) - the server's own 250ms rate limit is the real backstop.
const REACTION_DEBOUNCE_MS = 300;

// Press-and-hold tuning - see the plan doc for the full rationale. A hold
// gesture is: pointerdown -> (if held past HOLD_THRESHOLD_MS) intensity 1,
// then +1 every HOLD_STEP_MS up to HOLD_MAX_INTENSITY, until release.
const HOLD_THRESHOLD_MS = 400;
const HOLD_STEP_MS = 500;
const HOLD_MAX_INTENSITY = 4;
// If no repeat together_reaction_hold for the same emoji/sender arrives
// within this long, the receiving side auto-clears the visual - a
// self-healing timeout covering a sender whose tab dies mid-gesture while a
// second idle tab of theirs stays connected (so no hold_end WS event, and
// no server-side session-teardown cleanup, would otherwise ever arrive).
// Mirrors togetherSync.ts's own "client protects itself against a message
// that may never come" convention (unresolvableHandledRef).
const HOLD_RECEIVE_TIMEOUT_MS = 1800;

interface HoldState {
  emoji: string;
  phase: "waiting" | "holding";
  intensity: number;
  thresholdTimer: ReturnType<typeof setTimeout> | null;
  stepTimer: ReturnType<typeof setInterval> | null;
}

// Mounted once in AppRoot, always present, self-hiding via appState - same
// convention as Lightbox. Owns the socket handler registration
// (the single, per-user WS connection chat already maintains, extended
// with together_* events - see chatSocket.ts), the invite trigger/dialog,
// the incoming-invite banner, and the pending/active session status bar.
// No Lightbox.tsx changes: this composites over it via position:fixed at
// a z-index above MUI's default Modal (1300), reaching #mediaModalVideo
// (Phase 4) via a plain DOM lookup - same bundle, no bridge needed.
export function TogetherRoot() {
  const currentMediaIndex = useAppState((s) => s.currentMediaIndex);
  const media = useAppState((s) => s.media);
  const togetherSession = useAppState((s) => s.togetherSession);
  const togetherAwaitingRejoin = useAppState((s) => s.togetherAwaitingRejoin);
  const togetherIncomingInvite = useAppState((s) => s.togetherIncomingInvite);
  const togetherReactions = useAppState((s) => s.togetherReactions);
  const togetherHoldReaction = useAppState((s) => s.togetherHoldReaction);
  const togetherScheduleReminder = useAppState((s) => s.togetherScheduleReminder);
  const presenceUsers = useAppState((s) => s.presenceUsers);
  const mediaControlsHidden = useAppState((s) => s.mediaControlsHidden);
  const togetherPlaybackBlocked = useAppState((s) => s.togetherPlaybackBlocked);
  // Now opened from Lightbox.tsx's unified action bar, not a floating
  // button owned by this component - appState-driven (mirrors
  // gameDialogOpen's exact pattern) so any trigger can open it.
  const inviteDialogOpen = useAppState((s) => s.togetherInviteDialogOpen);
  const socketConnected = useSocketConnected();

  const [leaving, setLeaving] = useState(false);
  const [rejoining, setRejoining] = useState(false);
  const [respondingToInvite, setRespondingToInvite] = useState(false);
  const [startingScheduled, setStartingScheduled] = useState(false);
  const [reactionTrayDisabled, setReactionTrayDisabled] = useState(false);
  // useState alone isn't enough to gate rapid double-clicks/double-taps:
  // two synchronous click events both read the same stale (pre-update)
  // state value before React re-renders with disabled=true in between,
  // so both pass the check (confirmed via a real two-window test - a
  // synchronous double-click sent 2 requests, not 1). A ref is checked
  // and set synchronously, closing that gap; the state variable stays
  // just for the visual disabled styling.
  const reactionGuardRef = useRef(false);
  const [chatOpen, setChatOpen] = useState(false);

  // Own press-and-hold gesture state - rendered entirely locally (zero
  // round-trip), mirroring the existing tap-reaction "sender renders
  // optimistically" asymmetry. holdRef is the source of truth for the
  // in-flight gesture (timers, phase); ownHold is only what's rendered.
  const holdRef = useRef<HoldState | null>(null);
  const [ownHold, setOwnHold] = useState<{ emoji: string; intensity: number } | null>(null);

  // focusin/focusout bubble (unlike focus/blur), so this observes the
  // composer gaining/losing focus without TogetherChatInput.tsx needing its
  // own callback prop - used to keep the dock from auto-hiding (and thus
  // going pointer-events:none) while someone's mid-typing.
  const dockRef = useRef<HTMLDivElement>(null);
  const [dockFocused, setDockFocused] = useState(false);

  const { applyRemoteSync, discoverOrResyncTogetherSession } = useTogetherLiveSync(togetherSession, media, currentMediaIndex);

  const activeConversationId = togetherSession?.status === "active" ? togetherSession.conversationId : null;
  const { messages: chatMessages, streamQueue, appendSentMessage, removeFromStream } = useTogetherChatMessages(activeConversationId);

  // applyRemoteSync's identity changes every render (it's a plain closure,
  // not memoized), but this registration effect only needs to run once -
  // useTogetherLiveSync's own mediaRef keeps the function's DATA fresh
  // without needing to re-register it here.
  useEffect(() => {
    setTogetherSocketHandlers({
      onTogetherInvite: (data: TogetherInviteData) => applyTogetherInvite(data),
      onTogetherAccepted: (data: TogetherAcceptedData) => applyTogetherAccepted(data),
      onTogetherDeclined: (data: TogetherDeclinedData) => applyTogetherDeclined(data),
      onTogetherSync: (data) => applyRemoteSync(data),
      onTogetherReaction: (data: TogetherReactionData) => applyTogetherReaction(data),
      onTogetherReactionHold: (data: TogetherReactionHoldData) => applyTogetherReactionHold(data),
      onTogetherReactionHoldEnd: (data: TogetherReactionHoldEndData) => applyTogetherReactionHoldEnd(data),
      // Pauses via the EXISTING applyRemoteSync (same fields /sync already
      // uses) rather than a second, independent pause/seek code path - see
      // the plan doc for why reimplementing this would risk silently
      // skipping applyRemoteSync's echo-guard/autoplay-retry handling.
      onTogetherPauseAskStarted: (data: TogetherPauseAskStartedData) => {
        applyRemoteSync({
          sessionId: data.sessionId,
          filename: data.filename,
          mediaType: data.mediaType,
          position: data.position,
          playing: false,
          fromUserId: data.askedByUserId,
          updatedAt: data.updatedAt,
        });
        applyTogetherPauseAskStarted(data);
      },
      onTogetherPauseAskAnswered: (data: TogetherPauseAskAnsweredData) => applyTogetherPauseAskAnswered(data),
      onTogetherPauseAskEnded: (data: TogetherPauseAskEndedData) => applyTogetherPauseAskEnded(data),
      onTogetherEnded: (data: TogetherEndedData) => applyTogetherEnded(data),
      onTogetherPartnerDisconnected: (data: TogetherPartnerDisconnectedData) => applyTogetherPartnerDisconnected(data),
      onTogetherPartnerReconnected: (data: TogetherPartnerReconnectedData) => applyTogetherPartnerReconnected(data),
      onTogetherScheduleCreated: (data: TogetherScheduleCreatedData) => applyTogetherScheduleCreated(data),
      onTogetherScheduleUpdated: (data: TogetherScheduleUpdatedData) => applyTogetherScheduleUpdated(data),
      onTogetherScheduleCancelled: (data: TogetherScheduleCancelledData) => applyTogetherScheduleCancelled(data),
      onTogetherScheduleReady: (data: TogetherScheduleReadyData) => applyTogetherScheduleReady(data),
      onTogetherScheduleStarted: (data: TogetherScheduleStartedData) => applyTogetherScheduleStarted(data),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Don't let the chat toggle silently carry over "open" into a future
  // session just because it was left open at the end of a previous one.
  // Same reasoning for the blocked-playback affordance - a session ending
  // (leave, partner left, schedule swap) shouldn't leave a stale "tap to
  // resume" button floating over whatever's on screen next.
  useEffect(() => {
    if (!togetherSession) {
      setChatOpen(false);
      if (appState.togetherPlaybackBlocked) {
        appState.togetherPlaybackBlocked = false;
        notifyStateChange();
      }
    }
  }, [togetherSession]);

  function handleResumeTap(): void {
    const video = document.getElementById("mediaModalVideo") as HTMLVideoElement | null;
    appState.togetherPlaybackBlocked = false;
    notifyStateChange();
    video?.play()?.catch((err) => {
      logVideoPlayRejection("watch-together-manual-resume", video, togetherSession?.filename || "", err);
      showToast("Still couldn't play - try the video's own play button.", "error");
    });
  }

  // Auto-open the Lightbox on this side once the session goes active -
  // covers both the accepter (whose REST response already set status to
  // "active") and the inviter (who learns via the together_accepted push).
  // Skipped while togetherAwaitingRejoin is true - that flag means this
  // session was DISCOVERED (fresh boot/refresh, or a reconnect that found
  // a session this client didn't already know about), not just-joined via
  // accept/schedule-start, and passive discovery must never force-navigate.
  // TogetherRejoinPill's explicit tap (handleRejoinTap, below) is the only
  // thing that clears togetherAwaitingRejoin and opens the Lightbox in
  // that case.
  useEffect(() => {
    if (togetherSession?.status !== "active") return;
    if (togetherAwaitingRejoin) return;
    const idx = resolveMediaIndex(media, togetherSession.filename);
    if (idx === -1) {
      showToast("Can't find that item in your gallery.", "error");
      leaveTogetherApi(togetherSession.id);
      return;
    }
    appState.currentMediaIndex = idx;
    notifyStateChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [togetherSession?.status, togetherSession?.id, togetherAwaitingRejoin]);

  const lightboxOpen = currentMediaIndex >= 0 && currentMediaIndex < media.length;
  const currentItem = lightboxOpen ? media[currentMediaIndex] : null;

  // --- Own connection status, staged so a sub-3s blip never flashes
  // anything (a 500ms mobile network hiccup should not show
  // Disconnected/Reconnecting/Connected/Synced in quick succession). This
  // is distinct from togetherSession.partnerConnected (the PARTNER's
  // state, driven server-side) - this tracks the CURRENT user's own socket.
  // Escalates through 3 stages before offering Retry/Leave, so a normal
  // reconnect (the socket layer's own backoff caps at 30s) resolves
  // invisibly most of the time. ---
  type OwnConnStage = "connected" | "reconnecting" | "still_reconnecting" | "interrupted";
  const [ownConnStage, setOwnConnStage] = useState<OwnConnStage>("connected");

  useEffect(() => {
    let showTimer: ReturnType<typeof setTimeout> | null = null;
    let stillTimer: ReturnType<typeof setTimeout> | null = null;
    let interruptedTimer: ReturnType<typeof setTimeout> | null = null;
    function clearStageTimers(): void {
      if (showTimer) clearTimeout(showTimer);
      if (stillTimer) clearTimeout(stillTimer);
      if (interruptedTimer) clearTimeout(interruptedTimer);
    }
    function startEscalation(immediate: boolean): void {
      clearStageTimers();
      // navigator.onLine going false is a strong, fast signal (unlike a
      // bare WS close, which might be a normal reconnect cycle) - skip the
      // usual 3s grace and escalate to "reconnecting" right away.
      if (immediate) setOwnConnStage("reconnecting");
      else showTimer = setTimeout(() => setOwnConnStage("reconnecting"), 3000);
      stillTimer = setTimeout(() => setOwnConnStage("still_reconnecting"), 15000);
      interruptedTimer = setTimeout(() => setOwnConnStage("interrupted"), 45000);
    }
    const unsubscribeConn = onSocketConnectionChange((connected) => {
      clearStageTimers();
      if (connected) setOwnConnStage("connected");
      else startEscalation(false);
    });
    function onOffline(): void {
      startEscalation(true);
    }
    function onOnline(): void {
      // Actual "connected" only gets set once the socket itself reopens
      // (via onSocketConnectionChange above) - this just makes sure the
      // escalation timers are running again if the browser had settled
      // into a longer-since-offline state.
      startEscalation(false);
    }
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      unsubscribeConn();
      clearStageTimers();
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  function handleRetryConnection(): void {
    connectChatSocket();
    discoverOrResyncTogetherSession();
  }

  // --- Press & Hold reactions ---

  function endHold(): void {
    const hold = holdRef.current;
    if (!hold) return;
    if (hold.thresholdTimer) clearTimeout(hold.thresholdTimer);
    if (hold.stepTimer) clearInterval(hold.stepTimer);
    holdRef.current = null;
    setOwnHold(null);
    if (hold.phase === "holding" && togetherSession) {
      reactHoldEndApi(togetherSession.id, hold.emoji);
      // "Letting go" plays the familiar rise-and-fade release animation via
      // the same one-shot pathway taps already use.
      appState.togetherReactions = [
        ...appState.togetherReactions,
        { id: crypto.randomUUID(), emoji: hold.emoji.repeat(hold.intensity), fromUserId: appState.currentUserEmail },
      ];
      notifyStateChange();
    }
  }

  function startHold(emoji: string): void {
    if (!togetherSession) return;
    const hold: HoldState = { emoji, phase: "holding", intensity: 1, thresholdTimer: null, stepTimer: null };
    holdRef.current = hold;
    setOwnHold({ emoji, intensity: 1 });
    reactHoldApi(togetherSession.id, emoji, 1);
    navigator.vibrate?.(15);

    hold.stepTimer = setInterval(() => {
      const current = holdRef.current;
      if (!current) return;
      current.intensity = Math.min(HOLD_MAX_INTENSITY, current.intensity + 1);
      setOwnHold({ emoji: current.emoji, intensity: current.intensity });
      reactHoldApi(togetherSession.id, current.emoji, current.intensity);
      navigator.vibrate?.(15);
      if (current.intensity >= HOLD_MAX_INTENSITY && current.stepTimer) {
        clearInterval(current.stepTimer);
        current.stepTimer = null;
      }
    }, HOLD_STEP_MS);
  }

  function handleReactPointerDown(emoji: string): void {
    if (!togetherSession || togetherSession.status !== "active") return;
    endHold(); // clear any stray previous gesture first
    const thresholdTimer = setTimeout(() => startHold(emoji), HOLD_THRESHOLD_MS);
    holdRef.current = { emoji, phase: "waiting", intensity: 0, thresholdTimer, stepTimer: null };
  }

  function handleReactPointerUp(emoji: string): void {
    const hold = holdRef.current;
    if (!hold || hold.emoji !== emoji) return;
    const wasJustWaiting = hold.phase === "waiting";
    endHold();
    if (wasJustWaiting) handleReact(emoji); // released before the hold threshold - normal tap
  }

  function handleReactPointerLeaveOrCancel(emoji: string): void {
    const hold = holdRef.current;
    if (!hold || hold.emoji !== emoji) return;
    endHold(); // finger slid off the target - cancel entirely, no tap fallback
  }

  // Cleanup on unmount, tab backgrounding, or socket loss - a hold must
  // never render as visually "stuck" because its own release path never ran.
  useEffect(() => {
    function onVisibilityChange(): void {
      if (document.visibilityState === "hidden") endHold();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      endHold();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!socketConnected) endHold();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketConnected]);

  // Self-healing receive-side timeout - see HOLD_RECEIVE_TIMEOUT_MS's own
  // comment above.
  useEffect(() => {
    if (!togetherHoldReaction) return;
    const timer = setTimeout(() => {
      appState.togetherHoldReaction = null;
      notifyStateChange();
    }, HOLD_RECEIVE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [togetherHoldReaction]);

  useEffect(() => {
    const dock = dockRef.current;
    if (!dock) return;
    const onFocusIn = () => setDockFocused(true);
    const onFocusOut = () => setDockFocused(false);
    dock.addEventListener("focusin", onFocusIn);
    dock.addEventListener("focusout", onFocusOut);
    return () => {
      dock.removeEventListener("focusin", onFocusIn);
      dock.removeEventListener("focusout", onFocusOut);
    };
  }, [togetherSession?.status]);

  async function handleAccept(): Promise<void> {
    if (!togetherIncomingInvite) return;
    setRespondingToInvite(true);
    const result = await acceptTogetherApi(togetherIncomingInvite.sessionId);
    setRespondingToInvite(false);
    if (!result.ok) showToast(result.error || "Failed to accept invite.", "error");
  }

  async function handleDecline(): Promise<void> {
    if (!togetherIncomingInvite) return;
    setRespondingToInvite(true);
    await declineTogetherApi(togetherIncomingInvite.sessionId);
    setRespondingToInvite(false);
  }

  async function handleLeave(): Promise<void> {
    if (!togetherSession) return;
    setLeaving(true);
    await leaveTogetherApi(togetherSession.id);
    setLeaving(false);
  }

  // Explicit rejoin - TogetherRejoinPill's tap handler (the global,
  // outside-the-Together-screen affordance) and the "interrupted"
  // connection card's Retry button both use this. Validates with the
  // server FIRST, then navigates - never renders the Lightbox and
  // discovers it's dead afterward. Mirrors the auto-open effect's own
  // resolveMediaIndex + "can't find it, leave" fallback exactly, just
  // triggered by an explicit tap instead of a session-activation effect.
  async function handleRejoinTap(): Promise<void> {
    if (!togetherSession) return;
    setRejoining(true);
    const result = await rejoinTogetherApi(togetherSession.id);
    setRejoining(false);
    if (!result.ok) {
      showToast(result.error || "Couldn't rejoin.", "error");
      appState.togetherSession = null;
      appState.togetherAwaitingRejoin = false;
      notifyStateChange();
      return;
    }
    appState.togetherSession = result.session;
    appState.togetherAwaitingRejoin = false;
    const idx = resolveMediaIndex(media, result.session.filename);
    if (idx === -1) {
      showToast("Can't find that item in your gallery.", "error");
      notifyStateChange();
      await leaveTogetherApi(result.session.id);
      return;
    }
    // Set currentMediaIndex directly (not via applyRemoteSync) so the
    // Lightbox actually opens here - applyRemoteSync's own idx guard
    // deliberately refuses to open a CLOSED Lightbox (that's what stops
    // passive discovery from auto-hijacking navigation), but an explicit
    // Rejoin tap is exactly the case that guard is meant to let through.
    appState.currentMediaIndex = idx;
    notifyStateChange();
    // Then apply the session's actual position/playing/mediaType, the same
    // authoritative-state application every other sync path already uses -
    // without this, the video/photo opens but silently stays at whatever
    // its own natural start state is instead of where the session actually
    // is, which is the whole point of rejoining.
    applyRemoteSync({
      sessionId: result.session.id,
      filename: result.session.filename,
      mediaType: result.session.mediaType,
      position: result.session.position,
      playing: result.session.playing,
      fromUserId: result.session.peerUserId,
      updatedAt: result.session.updatedAt,
    });
  }

  function handleReact(emoji: string): void {
    if (!togetherSession || togetherSession.status !== "active") return;
    if (reactionGuardRef.current) return;
    reactionGuardRef.current = true;
    setReactionTrayDisabled(true);
    setTimeout(() => {
      reactionGuardRef.current = false;
      setReactionTrayDisabled(false);
    }, REACTION_DEBOUNCE_MS);
    reactTogetherApi(togetherSession.id, emoji);
  }

  async function handleStartScheduled(): Promise<void> {
    if (!togetherScheduleReminder) return;
    setStartingScheduled(true);
    const result = await startScheduledTogetherApi(togetherScheduleReminder.scheduleId);
    setStartingScheduled(false);
    if (result.ok) {
      appState.togetherScheduleReminder = null;
      notifyStateChange();
    } else {
      showToast(result.error || "Failed to start.", "error");
    }
  }

  function dismissScheduleReminder(): void {
    appState.togetherScheduleReminder = null;
    notifyStateChange();
  }

  // Don't fade the overlays while there's a reason someone needs them
  // visible/tappable right now: composer focused, chat scrollback open, an
  // active hold gesture, or Pause & Ask showing.
  const interactionActive = dockFocused || chatOpen || ownHold !== null || !!togetherSession?.pauseAndAsk;
  const controlsHiddenClass = mediaControlsHidden && !interactionActive ? " together-controls-hidden" : "";

  const partner = togetherSession ? (presenceUsers.find((u) => u.id === togetherSession.peerUserId) ?? null) : null;
  const partnerDisplayName = partner ? firstNameOf(partner.fullName, partner.email) : (togetherSession?.peerName ?? "");

  // Portaled to document.body, deliberately OUTSIDE #react-root - every
  // Dialog in this app (Lightbox included) pins container={#react-root}
  // and MUI's ModalManager marks every OTHER child of that container
  // aria-hidden="true" while any one of them is open, to trap focus for
  // accessibility. Since TogetherRoot's overlays render as plain siblings
  // there (not portaled), they were getting swept up in that too: z-index
  // still visually put them on top (confirmed via a real browser's
  // elementFromPoint), but Chrome refuses to move focus into an
  // aria-hidden subtree, so the chat input/message list/reaction tray
  // etc. were all silently unfocusable/untappable whenever the Lightbox
  // was open - which is always, since that's the whole point of this
  // feature. Rendering outside #react-root sidesteps the sweep entirely;
  // MUI Dialog's own portal target is independent of where its React
  // parent's DOM output lives, so AppDialog-based children (the invite
  // dialog) are unaffected by this and still portal into #react-root fine.
  return createPortal(
    <>
      {/* Trigger moved into Lightbox.tsx's unified action bar - this
          dialog stays here (still needs togetherSession/currentItem
          context from this component), just no longer renders its own
          floating entry button. */}
      <TogetherInviteDialog
        open={inviteDialogOpen}
        filename={currentItem?.filename || null}
        mediaType={currentItem ? (isVideoFilename(currentItem.filename) ? "video" : "image") : "image"}
        onClose={() => {
          appState.togetherInviteDialogOpen = false;
          notifyStateChange();
        }}
      />

      {/* "It's time!" prompt for a scheduled Watch Together whose unlock
          moment just arrived (together_schedule_ready push) - separate
          from the incoming-invite banner below, since this isn't an
          invite to respond to, it's an already-agreed plan whose lock
          just lifted. Hidden while already in an active session - can't
          start a second one anyway. */}
      {togetherScheduleReminder && !togetherSession && (
        <div
          className="glass p-3"
          style={{
            position: "fixed",
            bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1400,
            minWidth: 280,
            maxWidth: "90vw",
          }}
        >
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="bi bi-unlock-fill text-pink" aria-hidden="true" />
            <span className="fw-semibold">Your scheduled watch with {togetherScheduleReminder.peerName} is ready!</span>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <Button size="small" onClick={dismissScheduleReminder} disabled={startingScheduled}>
              Later
            </Button>
            <Button size="small" variant="contained" color="primary" onClick={handleStartScheduled} disabled={startingScheduled}>
              {startingScheduled ? "Starting…" : "Start Now"}
            </Button>
          </div>
        </div>
      )}

      {togetherIncomingInvite && (
        <div
          className="glass p-3"
          style={{
            position: "fixed",
            bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1400,
            minWidth: 280,
            maxWidth: "90vw",
          }}
        >
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="bi bi-people-fill text-pink" aria-hidden="true" />
            <span className="fw-semibold">{togetherIncomingInvite.fromName} wants to watch together</span>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <Button size="small" onClick={handleDecline} disabled={respondingToInvite}>
              Decline
            </Button>
            <Button size="small" variant="contained" color="primary" onClick={handleAccept} disabled={respondingToInvite}>
              Join
            </Button>
          </div>
        </div>
      )}

      {/* Every rich in-session surface below (sync badge through the dock/
          chat/reactions further down) is gated on lightboxOpen in addition
          to togetherSession - it only belongs on the actual Together
          screen. When the session is active but the Lightbox is closed
          (navigated away, or discovered-but-not-yet-rejoined),
          TogetherRejoinPill (rendered further down) is the only thing that
          shows instead - a compact, non-blocking affordance rather than
          this whole floating dock following the user around the app. */}

      {/* Centered sync badge - a status readout for THIS user's own
          connection (staged/debounced via ownConnStage so a sub-3s blip
          never flashes), not a control - sits in the dead space
          .media-fs-top already leaves empty between the back button and
          the pill once Together Mode hides Lightbox's own top-right
          controls. */}
      {togetherSession && lightboxOpen && (
        <div className={`together-sync-badge${controlsHiddenClass}`}>
          <i className="bi bi-arrow-repeat" aria-hidden="true" />
          <span className="together-sync-badge-text">
            <span className="together-sync-badge-title">Together</span>
            <span className="together-sync-badge-status">
              {togetherSession.status === "pending"
                ? "Connecting…"
                : ownConnStage === "connected"
                  ? "Synced"
                  : ownConnStage === "reconnecting"
                    ? "Reconnecting…"
                    : ownConnStage === "still_reconnecting"
                      ? "Still reconnecting…"
                      : "Connection interrupted"}
            </span>
          </span>
        </div>
      )}

      {togetherSession && lightboxOpen && (
        <div className={`together-status-pill${controlsHiddenClass}`}>
          <span className="together-status-avatar">
            {partner?.avatarFilename ? (
              <AvatarImage
                filename={partner.avatarFilename}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                fallback={partnerDisplayName.charAt(0).toUpperCase() || "?"}
              />
            ) : (
              partnerDisplayName.charAt(0).toUpperCase() || "?"
            )}
            {/* PARTNER's connection state, driven by the server's
                disconnect-grace tracking (togetherSession.partnerConnected)
                - NOT this client's own socket (that's ownConnStage/the sync
                badge above). Amber, not red, while reconnecting: this is
                the same self-healing grace-period recovery the backend
                already handles, not necessarily an error. */}
            <span
              title={togetherSession.partnerConnected ? "Connected" : "Reconnecting…"}
              aria-label={togetherSession.partnerConnected ? "Connected" : "Reconnecting"}
              className={`together-status-dot${togetherSession.partnerConnected ? " is-connected" : " is-reconnecting"}`}
            />
          </span>
          <span className="small fw-semibold text-truncate">
            {togetherSession.status === "pending"
              ? `Waiting for ${partnerDisplayName}…`
              : !togetherSession.partnerConnected
                ? `Reconnecting to ${partnerDisplayName}…`
                : partnerDisplayName}
          </span>
          {togetherSession.status === "active" && (
            <IconButton aria-label="Toggle chat" size="small" onClick={() => setChatOpen((v) => !v)} sx={{ flexShrink: 0, color: "inherit" }}>
              <i className="bi bi-chat-dots-fill" aria-hidden="true" />
            </IconButton>
          )}
          <IconButton
            aria-label="Leave together session"
            size="small"
            onClick={handleLeave}
            disabled={leaving}
            sx={{ flexShrink: 0, color: "inherit" }}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </IconButton>
        </div>
      )}

      {/* Only tier of the OWN-connection staging with actionable buttons -
          partner-disconnect state (the dot/text above) never gets buttons,
          since the current user can't act on the partner's device. Shown
          only after prolonged recovery failure (45s), never for a normal
          reconnect cycle. */}
      {togetherSession && lightboxOpen && ownConnStage === "interrupted" && (
        <div className={`together-status-pill together-connection-interrupted${controlsHiddenClass}`}>
          <span className="small fw-semibold">Connection interrupted</span>
          <Button size="small" onClick={handleRetryConnection} sx={{ color: "inherit" }}>
            Retry
          </Button>
          <Button size="small" onClick={handleLeave} disabled={leaving} sx={{ color: "inherit" }}>
            Leave
          </Button>
        </div>
      )}

      {/* Video playback audit, Bug A: a peer-triggered play() that Safari
          blocked for lacking a user gesture. Deliberately NOT gated behind
          controlsHiddenClass - unlike the status pill/dock (which fade
          during passive viewing), this only ever shows when playback is
          actually stuck, so it should stay visible and tappable until the
          tap itself resolves it. */}
      {togetherPlaybackBlocked && togetherSession?.status === "active" && lightboxOpen && (
        <button type="button" className="together-resume-tap" onClick={handleResumeTap}>
          <i className="bi bi-play-circle-fill" aria-hidden="true" />
          <span>Tap to resume in sync</span>
        </button>
      )}

      {/* One unified dock (reactions + Pause & Ask + composer) instead of
          independently-floating panels - a flex column lets the browser
          stack them correctly regardless of either row's actual height. */}
      {togetherSession?.status === "active" && activeConversationId && lightboxOpen && (
        <div ref={dockRef} className={`together-dock${controlsHiddenClass}`}>
          <div className="together-dock-reactions" role="group" aria-label="Send a reaction">
            {REACTION_EMOJIS.map((emoji) => (
              <IconButton
                key={emoji}
                size="small"
                aria-label={`React with ${emoji} - tap for a quick reaction, press and hold to intensify`}
                onPointerDown={() => handleReactPointerDown(emoji)}
                onPointerUp={() => handleReactPointerUp(emoji)}
                onPointerLeave={() => handleReactPointerLeaveOrCancel(emoji)}
                onPointerCancel={() => handleReactPointerLeaveOrCancel(emoji)}
                disabled={reactionTrayDisabled}
                className="together-reaction-btn"
                sx={{ fontSize: "1.15rem", p: 0.75 }}
              >
                {emoji}
              </IconButton>
            ))}
            <PauseAndAskOverlay sessionId={togetherSession.id} pauseAndAsk={togetherSession.pauseAndAsk} />
          </div>
          <TogetherChatInput conversationId={activeConversationId} onSent={appendSentMessage} embedded partnerName={partnerDisplayName} />
        </div>
      )}

      {/* Fading comment stream sits above the dock - chatting during a
          session was redesigned to never require opening a panel that
          covers the video/photo. The chat toggle in the status bar above
          opens TogetherChatPanel only as an opt-in "what did I miss"
          scrollback, not a required step to send a message. */}
      {activeConversationId && lightboxOpen && (
        <TogetherChatStream queue={streamQueue} partnerAvatarFilename={partner?.avatarFilename ?? null} onExpire={removeFromStream} />
      )}

      <TogetherChatPanel
        open={chatOpen && !!activeConversationId && lightboxOpen}
        messages={chatMessages}
        partnerAvatarFilename={partner?.avatarFilename ?? null}
        onClose={() => setChatOpen(false)}
      />

      {lightboxOpen &&
        togetherReactions.map((r) => <ReactionBurst key={r.id} emoji={r.emoji} onDone={() => removeTogetherReaction(r.id)} />)}

      {/* The PARTNER's currently-building hold reaction - own hold renders
          from local ownHold state above, zero round-trip. Different
          screen sides (own=left, peer=right) so both can render
          independently and simultaneously without overlapping. */}
      {togetherHoldReaction && lightboxOpen && (
        <HoldReactionBadge emoji={togetherHoldReaction.emoji} intensity={togetherHoldReaction.intensity} variant="peer" />
      )}
      {ownHold && lightboxOpen && <HoldReactionBadge emoji={ownHold.emoji} intensity={ownHold.intensity} variant="own" />}

      {/* The new global affordance for "active but not on the Together
          screen" - a compact, non-blocking pill instead of the full dock
          above. Rendered when the session is active (whether just-known-
          locally or freshly discovered via togetherAwaitingRejoin) and the
          Lightbox isn't currently open. */}
      {togetherSession?.status === "active" && !lightboxOpen && (
        <TogetherRejoinPill
          session={togetherSession}
          partnerDisplayName={partnerDisplayName}
          rejoining={rejoining}
          ending={leaving}
          onRejoin={handleRejoinTap}
          onEnd={handleLeave}
        />
      )}
    </>,
    document.body,
  );
}
